(() => {
  const rootPath = window.Shopify?.routes?.root || '/';
  const route = (path) => `${rootPath}${path}`.replace(/\/+/g, '/');
  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&':'&amp;', '<':'&lt;', '>':'&gt;', "'":'&#39;', '"':'&quot;',
  })[character]);
  const formatMoney = (cents, currency = window.Shopify?.currency?.active || 'USD') => {
    const wholeAmount = Math.round(cents / 100);
    if (currency === 'MMK') return `K${wholeAmount.toLocaleString('en-US')}`;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style:'currency', currency, minimumFractionDigits:0, maximumFractionDigits:0,
      }).format(wholeAmount);
    } catch (error) {
      return `${wholeAmount.toLocaleString()} ${currency}`;
    }
  };
  const request = async (path, options = {}) => {
    const response = await fetch(route(path), {
      ...options,
      headers:{ Accept:'application/json', ...(options.headers || {}) },
    });
    const text = await response.text();
    let payload = {};
    try { payload = text ? JSON.parse(text) : {}; } catch (error) { payload = {}; }
    if (!response.ok) throw new Error(payload.error || payload.description || payload.message || 'Something went wrong. Try again.');
    return payload;
  };

  const syncCartProductState = (cart) => {
    const inCart = new Set((cart.items || []).map((item) => String(item.product_id)));
    document.querySelectorAll('[data-product-card],[data-product-detail]').forEach((scope) => {
      const containsProduct = inCart.has(String(scope.dataset.productId));
      const wasInCart = scope.dataset.inCart === 'true';
      const sizes = [...scope.querySelectorAll('[data-size-option],[data-detail-size]')];
      const add = scope.querySelector('[data-add-to-cart],[data-detail-add]');
      const label = scope.querySelector('[data-add-label],[data-detail-add-label]');
      const status = scope.querySelector('[data-card-status],[data-detail-status]');
      if (containsProduct) {
        scope.dataset.inCart = 'true';
        sizes.forEach((button) => { button.disabled = true; });
        if (add) add.disabled = true;
        if (label) label.textContent = 'Already in cart';
        if (status) status.textContent = 'Remove this item from your cart to choose another size.';
        return;
      }
      if (!wasInCart || scope.dataset.dropLocked === 'true') return;
      delete scope.dataset.inCart;
      sizes.forEach((button) => {
        button.classList.remove('is-selected');
        button.disabled = button.dataset.variantAvailable === 'false';
      });
      if (add) add.disabled = true;
      if (label) label.textContent = 'Select a size';
      if (status) status.textContent = '';
    });
  };

  class IwbCartDrawer {
    constructor() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.backdrop = document.querySelector('[data-cart-backdrop]');
      this.items = this.drawer?.querySelector('[data-cart-items]');
      this.total = this.drawer?.querySelector('[data-cart-total]');
      this.message = this.drawer?.querySelector('[data-cart-message]');
      this.lastTrigger = null;
      if (!this.drawer || !this.backdrop) return;
      this.bind();
      this.refresh();
    }

    bind() {
      document.querySelectorAll('[data-cart-count]').forEach((node) => node.setAttribute('aria-live', 'polite'));
      document.querySelectorAll('.header-cart').forEach((link) => link.addEventListener('click', (event) => {
        event.preventDefault();
        this.lastTrigger = link;
        this.open();
        this.refresh();
      }));
      this.drawer.querySelectorAll('[data-cart-close]').forEach((button) => button.addEventListener('click', () => this.close()));
      this.backdrop.addEventListener('click', () => this.close());
      this.items.addEventListener('click', async (event) => {
        const remove = event.target.closest('[data-cart-remove]');
        if (!remove) return;
        remove.disabled = true;
        try {
          await request('cart/change.js', {
            method:'POST',
            headers:{ 'Content-Type':'application/json' },
            body:JSON.stringify({ id:remove.dataset.cartRemove, quantity:0 }),
          });
          await this.refresh();
        } catch (error) {
          this.message.textContent = error.message;
          remove.disabled = false;
        }
      });
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.close(); });
    }

    open() {
      this.backdrop.hidden = false;
      window.requestAnimationFrame(() => {
        this.backdrop.classList.add('is-open');
        this.drawer.classList.add('is-open');
      });
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('iwb-cart-open');
      this.drawer.querySelector('[data-cart-close]')?.focus();
    }

    close() {
      this.backdrop.classList.remove('is-open');
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('iwb-cart-open');
      window.setTimeout(() => { if (!this.backdrop.classList.contains('is-open')) this.backdrop.hidden = true; }, 250);
      this.lastTrigger?.focus();
    }

    async refresh() {
      const cart = await request('cart.js');
      this.render(cart);
      syncCartProductState(cart);
      return cart;
    }

    render(cart) {
      document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
      this.total.textContent = formatMoney(cart.total_price, cart.currency);
      this.message.textContent = '';
      if (!cart.items.length) {
        this.items.innerHTML = '<p class="iwb-cart-drawer__empty">Your cart is empty</p>';
        return;
      }
      this.items.innerHTML = cart.items.map((item) => `
        <article class="iwb-cart-line">
          <a class="iwb-cart-line__media" href="${escapeHtml(item.url)}" tabindex="-1" aria-hidden="true">${item.image ? `<img src="${escapeHtml(item.image)}" alt="" loading="lazy">` : ''}</a>
          <div class="iwb-cart-line__content">
            <p class="iwb-cart-line__vendor">${escapeHtml(item.vendor || '')}</p>
            <h3><a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a></h3>
            <p class="iwb-cart-line__meta">${item.variant_title && item.variant_title !== 'Default Title' ? `Size ${escapeHtml(item.variant_title)} · ` : ''}Qty ${item.quantity}</p>
            <div class="iwb-cart-line__bottom"><span>${formatMoney(item.final_line_price, cart.currency)}</span><button type="button" data-cart-remove="${escapeHtml(item.key)}" aria-label="Remove ${escapeHtml(item.product_title)}"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></button></div>
          </div>
        </article>`).join('');
    }

    async add({ variantId, productId }) {
      const current = await request('cart.js');
      const sameProduct = current.items.filter((item) => Number(item.product_id) === Number(productId));
      if (sameProduct.length) {
        syncCartProductState(current);
        throw new Error('Already in cart. Remove it from your cart to choose another size.');
      }
      await request('cart/add.js', {
        method:'POST',
        headers:{ 'Content-Type':'application/json' },
        body:JSON.stringify({ items:[{ id:Number(variantId), quantity:1 }] }),
      });
      await this.refresh();
      this.open();
    }
  }

  const bindProductDetail = (scope = document) => {
    scope.querySelectorAll('[data-product-detail]').forEach((product) => {
      if (product.dataset.initialized === 'true') return;
      product.dataset.initialized = 'true';
      const panels = [...product.querySelectorAll('[data-media-panel]')];
      const triggers = [...product.querySelectorAll('[data-media-trigger]')];
      triggers.forEach((trigger) => trigger.addEventListener('click', () => {
        const index = trigger.dataset.mediaTrigger;
        panels.forEach((panel) => {
          const active = panel.dataset.mediaPanel === index;
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
        });
        triggers.forEach((item) => item.classList.toggle('is-active', item === trigger));
      }));

      const sizes = [...product.querySelectorAll('[data-detail-size]')];
      const add = product.querySelector('[data-detail-add]');
      const label = product.querySelector('[data-detail-add-label]');
      const price = product.querySelector('[data-detail-price]');
      const status = product.querySelector('[data-detail-status]');
      let selected = null;
      sizes.forEach((button) => button.addEventListener('click', () => {
        sizes.forEach((item) => item.classList.remove('is-selected'));
        button.classList.add('is-selected');
        selected = button;
        add.disabled = false;
        label.textContent = 'Add to cart';
        price.textContent = button.dataset.price;
        status.textContent = '';
      }));
      add?.addEventListener('click', async () => {
        if (!selected || !window.IwbCart) return;
        add.disabled = true;
        label.textContent = 'Adding…';
        status.textContent = '';
        let added = false;
        try {
          await window.IwbCart.add({ variantId:Number(selected.dataset.variantId), productId:Number(product.dataset.productId) });
          added = true;
          label.textContent = 'Already in cart';
          status.textContent = 'Remove this item from your cart to choose another size.';
        } catch (error) {
          label.textContent = 'Add to cart';
          status.textContent = error.message;
        } finally {
          if (!added) add.disabled = false;
        }
      });
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    const cart = new IwbCartDrawer();
    window.IwbCart = { add:(item) => cart.add(item), open:() => cart.open(), refresh:() => cart.refresh() };
    bindProductDetail();
  });
  document.addEventListener('shopify:section:load', (event) => bindProductDetail(event.target));
})();
